import ThrottledApi from '../api/throttledApi';
import { AxiosResponse } from 'axios';
import moment from 'moment';

const extractFileNameFromContentDisposition = (contentDisposition: string | null | undefined, fallback: string): string => {
  if (!contentDisposition) {
    return fallback;
  }

  const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*([^;]+)/i);
  if (filenameStarMatch && filenameStarMatch[1]) {
    const rawValue = filenameStarMatch[1].replace(/["']/g, '').trim();
    const encodedValue = rawValue.split("''").pop() || rawValue;
    try {
      return decodeURIComponent(encodedValue);
    } catch {
      return encodedValue;
    }
  }

  const filenameMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/i) || contentDisposition.match(/filename\s*=\s*([^;]+)/i);
  if (filenameMatch && filenameMatch[1]) {
    return filenameMatch[1].replace(/["']/g, '').trim();
  }

  return fallback;
};

/**
 * Purchase Invoice Line Item interface matching API response
 */
export interface ApiPurchaseLineItem {
  line_number?: number;
  qty?: number;
  items_per_case?: number;
  item_code?: string | null;
  item_name?: string;
  pos_code?: string;
  case_price?: number;
  case_discount?: number;
  case_net?: number;
  crv?: number | null;
  total_wo_tax?: number;
  unit_cost?: number;
  new_selling_price?: number | null;
  prev_unit_cost?: number | null;
  previous_price?: number | null;
  prev_sell_price?: number | null;
  prev_margin?: number | null;
  margin?: number | null;
  matched_scan_code?: string;
  match_confidence?: number;
  status?: string;
  id?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Purchase Invoice interface matching API response
 */
export interface PurchaseInvoice {
  store_id: string;
  status: string;
  is_finalized?: boolean;
  invoice_number: string;
  invoice_date: string;
  customer_name: string;
  customer_address: string;
  vendor_name: string | null;
  vendor_address: string | null;
  vendor_phone: string | null;
  route: string;
  stop: string;
  customer_number: string;
  ship_to: string;
  chain_number: string | null;
  license: string;
  license_exp: string | null;
  terms: string;
  delivery: string;
  due_date: string | null;
  sales_rep: string;
  po_number: string | null;
  special_instructions: string | null;
  subtotal: number;
  tax: number;
  crv_total: number;
  grand_total: number;
  raw_ocr_text: string;
  id: string;
  uploaded_by: string;
  source_filename: string;
  stored_path: string;
  mime_type: string;
  file_size: number;
  processed_at: string | null;
  failed_at: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  items?: ApiPurchaseLineItem[]; // Line items from API (field name is "items" not "line_items")
}

/**
 * API Response for purchases list
 */
export interface PurchasesResponse {
  purchases: PurchaseInvoice[];
  total: number;
}

/**
 * Service Response wrapper
 */
export interface PurchasesServiceResponse {
  success: boolean;
  data: PurchasesResponse | null;
  error: any;
}

/**
 * Get purchases for a store
 * 
 * @param storeId - The ID of the store
 * @param limit - Maximum number of purchases to fetch (default: 100)
 * @param offset - Number of purchases to skip (default: 0)
 * @param startDate - Start date for filtering (optional)
 * @param endDate - End date for filtering (optional)
 * @returns Promise<PurchasesServiceResponse>
 */
export const getStorePurchases = async (
  storeId: string | null | undefined,
  limit: number = 100,
  offset: number = 0,
  startDate: Date | null = null,
  endDate: Date | null = null
): Promise<PurchasesServiceResponse> => {
  if (!storeId) {
    return {
      success: false,
      data: null,
      error: 'Store ID is required',
    };
  }

  try {
    let url = `/v1/purchases?store_id=${storeId}&limit=${limit}&offset=${offset}`;

    // Add date filters if provided
    if (startDate) {
      const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
      url += `&start_date=${formattedStartDate}`;
    }
    if (endDate) {
      const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
      url += `&end_date=${formattedEndDate}`;
    }

    const response = await ThrottledApi.get(url) as AxiosResponse<PurchasesResponse>;

    return {
      success: true,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('PurchasesService: Error fetching purchases:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message,
    };
  }
};

/**
 * Get a single purchase invoice by ID
 * 
 * @param purchaseId - The ID of the purchase invoice
 * @returns Promise with purchase invoice data
 */
export const getPurchaseInvoiceById = async (
  purchaseId: string | null | undefined
): Promise<{ success: boolean; data: PurchaseInvoice | null; error: any }> => {
  if (!purchaseId) {
    return {
      success: false,
      data: null,
      error: 'Purchase ID is required',
    };
  }

  try {
    const url = `/v1/purchases/${purchaseId}`;
    const response = await ThrottledApi.get(url) as AxiosResponse<PurchaseInvoice>;

    return {
      success: true,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('PurchasesService: Error fetching purchase invoice:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message,
    };
  }
};

/**
 * Upload a purchase invoice file
 * 
 * @param storeId - The ID of the store
 * @param file - The file to upload
 * @returns Promise with upload response
 */
export const uploadPurchaseInvoice = async (
  storeId: string | null | undefined,
  file: File
): Promise<{ success: boolean; data?: any; error?: string }> => {
  if (!storeId) {
    return {
      success: false,
      error: 'Store ID is required',
    };
  }

  if (!file) {
    return {
      success: false,
      error: 'File is required',
    };
  }

  try {
    const formData = new FormData();
    formData.append('file', file);

    const url = `/v1/purchases/upload?store_id=${storeId}`;
    const response = await ThrottledApi.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }) as AxiosResponse;

    return {
      success: true,
      data: response.data,
    };
  } catch (error: any) {
    console.error('PurchasesService: Error uploading purchase invoice:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to upload purchase invoice',
    };
  }
};

export const getPurchaseInvoiceFile = async (
  purchaseId: string
): Promise<{ success: boolean; data?: Blob; filename?: string; error?: string }> => {
  if (!purchaseId) {
    return {
      success: false,
      error: 'Purchase ID is required',
    };
  }

  try {
    const url = `/v1/purchases/${purchaseId}/file`;
    const response = (await ThrottledApi.get(url, { responseType: 'blob' })) as AxiosResponse<Blob>;
    const contentDisposition = response.headers['content-disposition'];
    const fallbackFilename = `purchase-invoice-${purchaseId}.pdf`;
    return {
      success: true,
      data: response.data,
      filename: extractFileNameFromContentDisposition(contentDisposition, fallbackFilename),
    };
  } catch (error: any) {
    console.error('PurchasesService: Error downloading purchase invoice file:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to download purchase invoice file',
    };
  }
};

/**
 * Extended interface for updating purchase line items (includes store_ids)
 */
export interface UpdatePurchaseLineItemPayload extends ApiPurchaseLineItem {
  store_ids?: string[];
}

/**
 * Interface for creating a new purchase line item
 */
export interface CreatePurchaseLineItemPayload {
  item_name: string;
  item_code?: string;
  qty: number;
  items_per_case: number;
  crv: number;
  case_price: number;
  case_discount: number;
  case_net: number;
  unit_cost: number;
  total: number;
}

/**
 * Interface for creating a pricebook queue entry
 */
export interface CreatePricebookQueuePayload {
  store_ids: string[];
  scan_code: string;
  item_name: string;
  new_selling_price: number;
  unit_cost: number;
}

export interface FinalizePurchasePayload {
  is_finalized: boolean;
  finalized_at: string;
}

/**
 * Create a new purchase line item
 *
 * @param purchaseId - The ID of the purchase invoice
 * @param payload - The line item data to create
 * @returns Promise<AxiosResponse>
 */
export const createPurchaseLineItem = async (
  purchaseId: string,
  payload: CreatePurchaseLineItemPayload
): Promise<AxiosResponse> => {
  if (!purchaseId) {
    throw new Error('Purchase ID is required');
  }
  try {
    const url = `/v1/purchases/${purchaseId}/items`;
    const response = await ThrottledApi.post(url, payload);
    return response as AxiosResponse;
  } catch (error: any) {
    console.error('PurchasesService: Error creating line item:', error);
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Failed to create purchase line item';
    throw new Error(message);
  }
};

/**
 * Update a specific purchase line item
 *
 * @param purchaseId - The ID of the purchase invoice
 * @param lineItemId - The ID of the line item to update
 * @param payload - The line item data to push to the API
 */
export const updatePurchaseLineItem = async (
  purchaseId: string,
  lineItemId: string,
  payload: UpdatePurchaseLineItemPayload
): Promise<AxiosResponse> => {
  if (!purchaseId) {
    throw new Error('Purchase ID is required');
  }

  if (!lineItemId) {
    throw new Error('Line item ID is required');
  }

  try {
    const url = `/v1/purchases/${purchaseId}/${lineItemId}`;
    const response = await ThrottledApi.put(url, payload);
    return response as AxiosResponse;
  } catch (error: any) {
    console.error('PurchasesService: Error updating line item:', error);
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Failed to update purchase line item';
    throw new Error(message);
  }
};

/**
 * Delete a purchase invoice
 *
 * @param purchaseId - The ID of the purchase invoice to delete
 * @returns Promise<void>
 */
export const deletePurchase = async (purchaseId: string): Promise<void> => {
  if (!purchaseId) {
    throw new Error('Purchase ID is required');
  }

  try {
    const url = `/v1/purchases/${purchaseId}`;
    await ThrottledApi.delete(url);
  } catch (error: any) {
    console.error('PurchasesService: Error deleting purchase invoice:', error);
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Failed to delete purchase invoice';
    throw new Error(errorMessage);
  }
};

/**
 * Store interface for purchase item stores
 */
export interface PurchaseItemStore {
  store_id: string;
  store_name: string;
  has_item: boolean;
  [key: string]: any; // Allow additional properties
}

/**
 * Purchase item detail response interface
 */
export interface PurchaseItemDetailResponse {
  item: ApiPurchaseLineItem;
  stores: PurchaseItemStore[];
  total_count: number;
}

/**
 * Get stores and item details for a specific purchase item
 * 
 * @param purchaseId - The ID of the purchase invoice
 * @param itemId - The ID of the purchase line item
 * @returns Promise with item details and stores data
 */
export const getPurchaseItemStores = async (
  purchaseId: string,
  itemId: string
): Promise<{ success: boolean; data: PurchaseItemDetailResponse | null; error: any }> => {
  if (!purchaseId) {
    return {
      success: false,
      data: null,
      error: 'Purchase ID is required',
    };
  }

  if (!itemId) {
    return {
      success: false,
      data: null,
      error: 'Item ID is required',
    };
  }

  try {
    const url = `/v1/purchases/items/stores?purchase_id=${purchaseId}&line_id=${itemId}`;
    const response = await ThrottledApi.get(url) as AxiosResponse<PurchaseItemDetailResponse>;

    return {
      success: true,
      data: response.data || null,
      error: null,
    };
  } catch (error: any) {
    console.error('PurchasesService: Error fetching purchase item stores:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message,
    };
  }
};

/**
 * Get stores and item details for a specific purchase item by scan code
 * 
 * @param purchaseId - The ID of the purchase invoice
 * @param scanCode - The scan code (UPC) to search for
 * @param lineId - The ID of the purchase line item (optional)
 * @param storeId - The ID of the store (optional)
 * @returns Promise with item details and stores data
 */
export const getPurchaseItemStoresByScanCode = async (
  purchaseId: string,
  scanCode: string,
  lineId?: string,
  storeId?: string
): Promise<{ success: boolean; data: PurchaseItemDetailResponse | null; error: any }> => {
  if (!purchaseId) {
    return {
      success: false,
      data: null,
      error: 'Purchase ID is required',
    };
  }

  if (!scanCode) {
    return {
      success: false,
      data: null,
      error: 'Scan code is required',
    };
  }

  try {
    const params = new URLSearchParams({
      purchase_id: purchaseId,
      scan_code: scanCode,
    });

    if (lineId) {
      params.append('line_id', lineId);
    }

    if (storeId) {
      params.append('store_id', storeId);
    }

    const url = `/v1/purchases/items/stores?${params.toString()}`;
    const response = await ThrottledApi.get(url) as AxiosResponse<PurchaseItemDetailResponse>;

    return {
      success: true,
      data: response.data || null,
      error: null,
    };
  } catch (error: any) {
    console.error('PurchasesService: Error fetching purchase item stores by scan code:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message,
    };
  }
};

/**
 * Create a pricebook queue entry for a purchase line item
 *
 * @param purchaseId - The ID of the purchase invoice
 * @param lineItemId - The ID of the line item
 * @param payload - The data for the pricebook queue
 */
export const createPricebookQueue = async (
  purchaseId: string,
  lineItemId: string,
  payload: CreatePricebookQueuePayload
): Promise<AxiosResponse> => {
  if (!purchaseId) {
    throw new Error('Purchase ID is required');
  }

  if (!lineItemId) {
    throw new Error('Line item ID is required');
  }

  try {
    const url = `/v1/purchases/${purchaseId}/${lineItemId}/create-pricebook-queue`;
    const response = await ThrottledApi.post(url, payload);
    return response as AxiosResponse;
  } catch (error: any) {
    console.error('PurchasesService: Error creating pricebook queue:', error);
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Failed to create pricebook queue';
    throw new Error(message);
  }
};

/**
 * Finalize a purchase invoice
 *
 * @param storeId - The ID of the store
 * @param purchaseId - The ID of the purchase invoice
 * @param payload - Finalize payload
 * @returns Promise<AxiosResponse>
 */
export const finalizePurchase = async (
  storeId: string,
  purchaseId: string,
  payload: FinalizePurchasePayload
): Promise<AxiosResponse> => {
  if (!storeId) {
    throw new Error('Store ID is required');
  }

  if (!purchaseId) {
    throw new Error('Purchase ID is required');
  }

  try {
    const url = `/v1/stores/${storeId}/purchases/${purchaseId}/finalized`;
    const response = await ThrottledApi.post(url, payload);
    return response as AxiosResponse;
  } catch (error: any) {
    console.error('PurchasesService: Error finalizing purchase:', error);
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Failed to finalize purchase';
    throw new Error(message);
  }
};

